import React, { useState, useEffect } from "react";
import {
  DropdownContainer,
  EmailAvatar,
  EmailInput,
  EmailPill,
  EmailText,
  ErrorContainer,
  Image,
  Input,
  Label,
  MessageContainer,
  MessageDescription,
  MessageTitle,
  ModalContainer,
  ModalContent,
  ModalFooter,
  PreviouslySharedContainer,
  PreviouslySharedItem,
  PreviouslySharedTitle,
  SharedEmailText,
} from "./index.styled";
import { Loader } from "../../App";
import CheckSuccessImg from "../../Assets/Images/CheckSuccess.png";
import ErrorFailedImg from "../../Assets/Images/Error.png";
import { Title } from "../Popup/index.styled";
import {
  ModalCloseButton,
  StyledButton,
} from "../AdvanceFilterComp/index.styled";
import { ReactComponent as CrossSolid } from "../../Assets/Icons/CrossSolid.svg";
import {
  DropdownButton,
  SelectedItem,
  SelectedItemText,
} from "../HospitalComparisonDropdown/index.sc";
import OptionsList from "../OptionsList";
import { ReactComponent as Arrow } from "../../Assets/Icons/Arrow.svg";
import { addNewEmail, getSharedEmails } from "./utils";
import { viewSizeCalculator } from "../../utils";
import { useMutation } from "@tanstack/react-query";
import { sendBookmarkEmail } from "../../services/apiService";

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const bgColor = ["#FDDDD7", "#E9D75D", "#CCE1FD"];
const BookmarkSharedModal = ({
  toggleModal,
  options,
  setIsModalOpen,
  bookmarksList: bookMarkedData,
}) => {
  const [recepientEmails, setRecepientEmails] = useState(new Set());
  const [recepientEmailInput, setRecipientEmailInput] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isValidForm, setIsValidForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMailSentSuccess, setIsMailSentSuccess] = useState(false);
  const [isMailSentFailure, setIsMailSentFailure] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [recentSharedEmails, setRecentSharedEmails] = useState(
    getSharedEmails() || [],
  );

  const handleEmailChange = (e) => {
    const enteredEmail = e.target.value;
    setRecipientEmailInput(enteredEmail);
    setErrorMessage("");
  };

  const handleOutsideClick = () => {
    setIsModalOpen(false);
  };

  const handleOutsideDropdownClick = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
  };

  const addEmail = () => {
    if (recepientEmailInput.trim() !== "") {
      if (emailRegex.test(recepientEmailInput)) {
        setRecepientEmails(
          (prevSet) => new Set([...prevSet, recepientEmailInput]),
        );
        setRecentSharedEmails(addNewEmail(recepientEmailInput));
        setRecipientEmailInput("");
        setErrorMessage("");
      } else {
        setErrorMessage("Enter a valid email");
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && recepientEmailInput.trim() !== "") {
      addEmail();
    }
  };

  const handleBlur = () => {
    addEmail();
  };

  const handleOptionClick = (option) => {
    const allBookmarkedLabel = `All Bookmarked Hospitals (${
      options.length - 1
    })`;
    let newSelected = [];

    if (option.value === allBookmarkedLabel) {
      newSelected =
        selectedOptions.length === options.length
          ? []
          : options.map((o) => o.value);
    } else {
      if (selectedOptions.includes(allBookmarkedLabel)) {
        newSelected = selectedOptions.filter(
          (val) => val !== allBookmarkedLabel && val !== option.value,
        );
      } else {
        newSelected = selectedOptions.includes(option.value)
          ? selectedOptions.filter((val) => val !== option.value)
          : [...selectedOptions, option.value];

        if (newSelected.length === options.length - 1) {
          newSelected = options.map((o) => o.value);
        }
      }
    }

    setSelectedOptions(newSelected);
  };

  const removeSelectedEmail = (emailToRemove) => {
    setRecepientEmails((prevSet) => {
      const set = new Set(prevSet);
      set.delete(emailToRemove);
      return set;
    });
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getSelectedBookmarkedData = () => {
    return bookMarkedData.filter((item) => selectedOptions.includes(item.name));
  };

  const generateEmailBody = () => {
    let emailBody = `
    <div style="font-family: Arial, sans-serif; line-height: 1.6;">
      <h2 style="color: #2c3e50; text-align: center;">List of Bookmarked Medical Centers</h2>
      <ul style="padding-left: 20px;">
  `;
    const data = getSelectedBookmarkedData();
    data.forEach((item) => {
      emailBody += `
      <li style="margin-bottom: 15px; padding: 10px; border-bottom: 1px solid #ddd;">
        <strong style="font-size: 16px; color: #34495e;">${item.name}</strong><br />
        <span style="font-size: 14px; color: #7f8c8d;">${item.address}</span> <br />
        <a href="${item.link}" style="color: #2980b9; text-decoration: none;">🔗 Visit Website</a>
      </li>
    `;
    });

    emailBody += `</ul></div>`;

    return emailBody;
  };

  const sendEmailMutation = useMutation({ mutationFn: sendBookmarkEmail });

  const sendEmailToRecipient = async () => {
    setIsSending(true);
    try {
      const result = await sendEmailMutation.mutateAsync({
        to: Array.from(recepientEmails),
        subject: "Your Bookmarked Medical Centers Data",
        html: generateEmailBody(),
        from: "kritish@rhombuz.io",
      });
      if (result.success) {
        setIsMailSentSuccess(true);
      } else {
        setIsMailSentFailure(true);
      }
    } catch (error) {
      console.log("Error sending email:", error);
      setIsMailSentFailure(true);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    setIsValidForm(recepientEmails.size > 0 && selectedOptions.length > 0);
  }, [selectedOptions, recepientEmails]);

  return (
    <ModalContainer onClick={handleOutsideClick}>
      {isMailSentSuccess ? (
        <ModalContent width={32}>
          <ModalCloseButton>
            <CrossSolid onClick={toggleModal} />
          </ModalCloseButton>

          <MessageContainer>
            <Image src={CheckSuccessImg} alt="" />
            <MessageTitle>Successfully Shared!</MessageTitle>
            <MessageDescription>
              Selected bookmarks shared successfully
            </MessageDescription>
          </MessageContainer>
        </ModalContent>
      ) : isMailSentFailure ? (
        <ModalContent>
          <div style={{ textAlign: "right", cursor: "pointer" }}>
            <CrossSolid onClick={toggleModal} />
          </div>

          <MessageContainer>
            <Image src={ErrorFailedImg} alt="" />
            <MessageTitle>Failure to Shared!</MessageTitle>
            <MessageDescription>
              Selected bookmarks has failed to shared
            </MessageDescription>
          </MessageContainer>
        </ModalContent>
      ) : (
        <ModalContent onClick={handleOutsideDropdownClick}>
          <Title>Share Bookmarks</Title>
          <EmailInput>
            {Array.from(recepientEmails).map((emailItem) => (
              <EmailPill key={emailItem}>
                <EmailText>{emailItem}</EmailText>
                <CrossSolid onClick={() => removeSelectedEmail(emailItem)} />
              </EmailPill>
            ))}
            <Input
              placeholder="Add other by E-mail"
              type="email"
              value={recepientEmailInput}
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              isFullWidth={recepientEmails.size === 0}
              autoComplete="new-email"
            />
          </EmailInput>

          <ErrorContainer>
            {errorMessage ? (
              <Label isError={true}>{errorMessage}</Label>
            ) : (
              <Label>Press "Enter/Tab" to add new email</Label>
            )}
          </ErrorContainer>

          <PreviouslySharedContainer>
            <PreviouslySharedTitle>
              People previously shared with
            </PreviouslySharedTitle>

            <div>
              {recentSharedEmails.map((item, index) => (
                <PreviouslySharedItem
                  key={item?.name}
                  onClick={() => {
                    setRecepientEmails(
                      (prevSet) => new Set([...prevSet, item?.email]),
                    );
                  }}
                >
                  <EmailAvatar bgColor={bgColor[index % bgColor.length]}>
                    {item?.name?.charAt(0)}
                  </EmailAvatar>
                  <SharedEmailText>{item?.name}</SharedEmailText>
                </PreviouslySharedItem>
              ))}
            </div>
          </PreviouslySharedContainer>

          <DropdownContainer onClick={toggleDropdown}>
            {Array.isArray(options) && (
              <SelectedItem>
                <div>
                  {selectedOptions.length === 0 ? (
                    <span>Select Hospitals</span>
                  ) : selectedOptions.length === 1 ? (
                    selectedOptions[0]
                  ) : selectedOptions.length === options.length ? (
                    `All Bookmarked Hospitals (${options.length - 1})`
                  ) : (
                    `${selectedOptions.length} Hospitals Selected`
                  )}
                </div>
                <DropdownButton>
                  <Arrow />
                </DropdownButton>
              </SelectedItem>
            )}

            {Array.isArray(options) && (
              <OptionsList
                options={options}
                onOptionClick={handleOptionClick}
                selectedOption={selectedOptions}
                isOpen={isDropdownOpen}
                customPadding={0.313}
                customBorderRadius={0.938}
                showCheckbox={true}
                height={15}
                top={3.7}
                isMargin={true}
              />
            )}
          </DropdownContainer>

          <ModalFooter>
            <StyledButton width={6.5} onClick={toggleModal}>
              Cancel
            </StyledButton>
            <StyledButton
              onClick={sendEmailToRecipient}
              disabled={!isValidForm}
              width={6.5}
              style={{
                backgroundColor: isValidForm ? "#333333" : "#ccc",
                cursor: isValidForm ? "pointer" : "not-allowed",
                color: isValidForm ? "#fff" : "#000",
              }}
            >
              {isSending ? (
                <Loader 
                  height={viewSizeCalculator(1)}
                  width={viewSizeCalculator(1)}
                  isTransparent={true}
                />
              ) : (
                "Share"
              )}
            </StyledButton>
          </ModalFooter>
        </ModalContent>
      )}
    </ModalContainer>
  );
};

export default BookmarkSharedModal;
